import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TrendingHottestItem } from "../shared/type";
import { LocalizedLink } from "../i18n/LocaleRouting";
import { trendingItemPathFromItem } from "../utils/trendingItemId";

const TrendingNow: React.FC<{
  trendingData: TrendingHottestItem[];
  className?: string;
  isShow?: boolean;
}> = ({ trendingData, className, isShow }) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [isTitleVisible, setIsTitleVisible] = useState(false);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [itemVisibleStates, setItemVisibleStates] = useState<boolean[]>(
    new Array(trendingData.length).fill(false),
  );
  const rankColors = ["text-[#FF0000]", "text-[#fff600]", "text-[#00FFFF]"];

  useEffect(() => {
    // Observe title
    if (titleRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsTitleVisible(true);
            observer.unobserve(titleRef.current!);
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(titleRef.current);
      return () => observer.disconnect();
    }
  }, []);

  useEffect(() => {
    setItemVisibleStates(new Array(trendingData.length).fill(false));
    itemRefs.current = [];
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [trendingData]);

  useEffect(() => {
    // Observe each item
    const observers: IntersectionObserver[] = [];
    itemRefs.current.forEach((ref, index) => {
      if (!ref) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setItemVisibleStates((prev) => {
              const newStates = [...prev];
              newStates[index] = true;
              return newStates;
            });
            observer.unobserve(ref);
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [trendingData.length, itemVisibleStates.length]);

  return (
    <>
      {/* Trending Now */}
      {trendingData && (
        <div className={`text-[#fff] space-y-2 ${className}`}>
          <h1
            ref={titleRef}
            className={`flex ${
              isShow
                ? "justify-start ml-3 lg:text-5xl text-2xl "
                : "lg:text-2xl text-xl "
            } items-center gap-x-1 fade-in-section ${
              isTitleVisible ? "fade-in-visible" : ""
            }`}
          >
            {/* <img src="/icon/hot.png" alt="hot" className="w-10 h-10" /> */}
            <span className="lg:hidden">🔥</span>
            <b>{t("home.trending")}</b>
          </h1>
          <div
            ref={listRef}
            className={`flex flex-col p-3 rounded-md ${
              isShow
                ? "w-ful h-[408px] sm:h-[580px]"
                : "bg-[#f4f1ecaa] xl:max-w-[400px] lg:max-w-[340px] max-w-[280px]"
            } backdrop-blur-sm backdrop-opacity-70 xl:max-h-[480px] lg:max-h-[440px] md:max-h-[340px] overflow-y-scroll scrollbar-hide`}
          >
            {trendingData.map((item, index) => {
              return (
                <LocalizedLink
                  key={item.id}
                  target="_top"
                  to={trendingItemPathFromItem(item)}
                  className={`lg:pb-2.5 lg:pt-2.5 last:pb-1.5 py-1.5 first:pt-0.5 fade-in-section cursor-pointer ${
                    itemVisibleStates[index] ? "fade-in-visible" : ""
                  } ${
                    index === trendingData.length - 1
                      ? "border-b-0"
                      : "border-b-[1px] border-[#808080]"
                  } ${isShow ? "  border-[#ffffff]" : " "}`}
                  style={{
                    transitionDelay: `${index * 0.1}s`,
                  }}
                >
                  <div className="flex items-center gap-x-4 w-full bg-white/0">
                    <div
                      className={`relative ${
                        isShow
                          ? "w-[100px] h-[80px] sm:w-[120px] sm:h-[100px]"
                          : "2xl:w-[100px] 2xl:h-[90px] w-[90px] lg:h-[80px] h-[70px]"
                      } pl-[2px] border-r-[1px] border-[#7c7c7c] flex-shrink-0`}
                    >
                      {/* TOP 排名 */}
                      {index < 3 && (
                        <div className="absolute md:-top-3 -top-5 -left-2 w-[50px] h-[60px] z-10">
                          <i
                            className={`absolute scale-x-100 scale-y-125 inset-0 flex items-center justify-center font-extrabold lg:text-[48px] text-[42px] font-serif ${rankColors[index]} leading-none`}
                          >
                            {index + 1}
                          </i>
                        </div>
                      )}

                      {/* 商品圖 */}
                      <img
                        src={item.thumbnailUrl}
                        alt={item.name}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        className="w-full h-full object-cover select-none [-webkit-user-drag:none]"
                      />
                    </div>
                    <div className="flex flex-col gap-y-2">
                      <h6
                        className={`font-bold ${
                          isShow
                            ? "text-[#ffffff] md:text-base sm:text-sm text-xs"
                            : "text-black xl:text-base lg:text-sm text-xs lg:line-clamp-3"
                        }`}
                      >
                        {item.name}
                      </h6>
                    </div>
                  </div>
                </LocalizedLink>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default TrendingNow;
